import React, { useState } from 'react';
import { FeatureRestrictionWrapperProps } from '@/models/feature-restriction.model';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { FEATURE_DESCRIPTIONS } from '@/constants/FeatureAccessConfig';
import { Crown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ChipComponent from '@/components/ui/ChipComponent';
import './FeatureRestrictionWrapper.scss';
import commonService from '@/services/common.service';

const FeatureRestrictionWrapper: React.FC<FeatureRestrictionWrapperProps> = ({
  feature,
  children,
  fallback,
  showUpgradePrompt = true,
  className = '',
}) => {
  const { hasAccess, userTier, getRequiredTierForFeature } = useFeatureAccess();
  const [showRestriction, setShowRestriction] = useState(false);

  // Check if user has access to the feature
  const hasFeatureAccess = hasAccess(feature);

  // If user has access, render the children
  if (hasFeatureAccess) {
    return <>{children}</>;
  }

  // If user doesn't have access, show locked version with clickable overlay
  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  const requiredTier = getRequiredTierForFeature(feature);

  return (
    <div className={`feature-restriction-wrapper locked ${className}`}>
      {/* Content with crown icon */}
      <div className="restricted-content">
        {children}
      </div>
      
      {/* Invisible overlay that captures all clicks */}
      <div 
        className="invisible-overlay"
        onClick={() =>{ setShowRestriction(true)
          commonService.handleMixpanelTrack("Feature restriction clicked", {
            feature: feature,
          });
        }}
      />
      
      {/* Crown icon indicator */}
      <div className="crown-indicator">
        <Crown className="w-4 h-4 crown-icon" />
      </div>

      {/* Restriction dialog using proper Dialog component */}
      <Dialog open={showRestriction} onOpenChange={setShowRestriction} >
        <DialogContent className="restriction-dialog-content">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              Upgrade Required
            </DialogTitle>
            <DialogDescription>
              This feature requires a higher subscription plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="feature-info">
              <h3 className="font-semibold text-lg">{FEATURE_DESCRIPTIONS[feature].name}</h3>
              <p className="text-sm text-muted-foreground">{FEATURE_DESCRIPTIONS[feature].description}</p>
            </div>
            
            <div className="upgrade-info">
              <div className="tier-comparison">
                <div className="current-tier">
                  <ChipComponent 
                    text={userTier} 
                    variant="default"
                  />
                  <span className="tier-label">Current</span>
                </div>
                
                <div className="upgrade-arrow">
                  <div className="arrow-line"></div>
                  <Crown className="w-3 h-3 arrow-crown" />
                </div>
                
                <div className="required-tier">
                  <ChipComponent 
                    text={requiredTier} 
                    variant="info"
                  />
                  <span className="tier-label">Required</span>
                </div>
              </div>
            </div>
            
            <div className="upgrade-message space-y-2">
              <p className="text-sm">{FEATURE_DESCRIPTIONS[feature].upgradeMessage}</p>
              <p className="text-sm text-muted-foreground">{FEATURE_DESCRIPTIONS[feature].upgradeDescription}</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 mt-4">
            <button
              onClick={() => setShowRestriction(false)}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => {
                window.location.href = '/pricing';
                setShowRestriction(false);
              }}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Crown className="w-4 h-4" />
              Upgrade Now
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeatureRestrictionWrapper;
